/**
 * Whether a key just saved actually opens its provider — asked once, on Save, of a
 * call that costs nothing.
 *
 * Saving any string used to print "Connected. From here every node is live
 * reasoning", so a mistyped key surfaced only on the first paid pass, as an error
 * over a question the reader had already typed. Both houses the page can reach
 * list their models on an authenticated GET that bills nothing, and both answer a
 * browser:
 *
 *  - Anthropic's `GET /v1/models`, with the same direct-browser header the SDK
 *    sends on every pass. Measured from this sandbox: the preflight allows the
 *    three headers from any origin, and a rejected key comes back as a 401 whose
 *    body — "API key is invalid." — the page can read.
 *  - OpenAI's `GET /v1/models`, the same list on the same host the adapter
 *    already calls from the page. Not reachable from the sandbox this was written
 *    in, so its CORS is taken on the adapter's own evidence rather than measured;
 *    a refusal there reads as `unchecked`, never as a verdict on the key.
 *
 * What this can say is deliberately small. A 2xx is "the key works" — it opened
 * the door, and says nothing about credits, which only a pass can find out. A
 * 401 is the provider's own sentence about the key. Everything else — a 403, a
 * network that blocks the host, a timeout, a 429, a 5xx, a browser that refused
 * the response — is `unchecked`, and the sheet says the key was saved and will be
 * checked on the first pass: a check that could not run is never reported as a
 * key that failed, and the key is saved either way (the card never refuses one).
 *
 * The custom endpoint and the judge are not asked: the first is somebody's own
 * server, whose free calls this app cannot know, and the second is a host the
 * page reaches only through a build's endpoint (D107).
 *
 * Not a model call. Nothing is generated, nothing is billed, and the ledger does
 * not see it; it is the Save press's own request, to the host the card's sentence
 * already names as where the key goes.
 */

import type { ProviderId } from '../core/models'
import { STATUS_WORD } from '../core/vocabulary'

export type KeyCheck =
  { kind: 'works' } | { kind: 'rejected'; message: string } | { kind: 'unchecked' }

/** Long enough for a slow network, short enough that Save does not feel stuck. */
export const KEY_CHECK_TIMEOUT_MS = 6000

/** The free, authenticated request each checkable house answers. */
const FREE_CALL: Partial<
  Record<ProviderId, (key: string) => { url: string; headers: Record<string, string> }>
> = {
  anthropic: (key) => ({
    url: 'https://api.anthropic.com/v1/models?limit=1',
    headers: {
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
  }),
  openai: (key) => ({
    url: 'https://api.openai.com/v1/models',
    headers: { Authorization: `Bearer ${key}` },
  }),
}

/** Whether a Save on this provider's card can be checked at all. */
export const keyCheckable = (provider: ProviderId): boolean => FREE_CALL[provider] !== undefined

/** The provider's own sentence, out of either house's error envelope. */
function providerMessage(body: unknown): string | null {
  const e = (body as { error?: { message?: unknown } } | null)?.error
  const m = typeof e?.message === 'string' ? e.message.trim() : ''
  return m ? m.slice(0, 300) : null
}

export async function checkKey(
  provider: ProviderId,
  key: string,
  opts: { timeoutMs?: number; fetch?: typeof globalThis.fetch } = {},
): Promise<KeyCheck> {
  const call = FREE_CALL[provider]
  if (!call || !key.trim()) return { kind: 'unchecked' }
  const { url, headers } = call(key.trim())
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? KEY_CHECK_TIMEOUT_MS)
  try {
    const res = await (opts.fetch ?? globalThis.fetch)(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    })
    if (res.ok) return { kind: 'works' }
    // 401 only. A 403 is a key that authenticated and lacks a permission — an
    // OpenAI project key without the models scope reads this way and can still
    // run every pass — so it is no verdict on whether the key works.
    if (res.status === 401) {
      const body: unknown = await res.json().catch(() => null)
      return { kind: 'rejected', message: providerMessage(body) ?? 'The key was rejected.' }
    }
    return { kind: 'unchecked' }
  } catch {
    return { kind: 'unchecked' }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * The one sentence Save says about a key it wrote, from what the check found.
 *
 * "Connected. From here every node is live reasoning" was printed for any string,
 * which is the finding this replaces: the sentence now says only what happened —
 * the provider accepted the key, or the key was saved and will meet its provider
 * on the first pass. The model is named only when the key belongs to the house
 * the selection runs on, for the reason the sheet has always had: a second
 * provider's card can be the one that wrote.
 */
export function keySavedSentence(o: {
  house: string
  replaced: boolean
  verdict: KeyCheck | null
  /** The selected model's name, when the key is its house's. */
  runs?: string
}): string {
  if (o.verdict?.kind === 'works') {
    return `${STATUS_WORD.keyWorks} — ${o.house} accepted ${o.replaced ? 'the new key' : 'it'}.${o.runs ? ` The next pass runs on ${o.runs}.` : ''}`
  }
  return o.replaced
    ? `${o.house} key replaced · ${CHECKED_ON_FIRST_PASS}.`
    : o.runs
      ? `${STATUS_WORD.keySaved} · ${CHECKED_ON_FIRST_PASS}.`
      : `${o.house} ${STATUS_WORD.keySaved.toLowerCase()} · ${CHECKED_ON_FIRST_PASS}.`
}

/** Said when no free call could vouch for the key, which the first pass then will. */
const CHECKED_ON_FIRST_PASS = 'checked on your first pass'

/** What a card prints under its field when its provider turned the key away. */
export const keyRejectedLine = (house: string, message: string): string =>
  `${house} rejected this key: “${message}” It is saved as typed — paste another to replace it.`
